/**
 * Wire contract for the admission firewall ruleset store (#1758).
 *
 * Two surfaces share it:
 *
 *   1. `/internal/firewall-rulesets/*` (gateway-worker) — owns the tables,
 *      the write-time validation, and the `active_version` pointer.
 *   2. `/v1/ops/firewall-rulesets/*` (ops-api) — role-gated proxy, plus the
 *      `ops_audit` mirror of the gateway mutation receipt.
 *
 * The shape encodes the one property the whole design rests on: a version is a
 * DRAFT. Writing one changes no request's verdict. `active_version` is what the
 * gateway reads, and moving it is a separate, separately-audited call.
 *
 * `rules` is carried as an opaque JSON value rather than a mirrored copy of
 * `@nadoutong/firewall`'s `FirewallRule`. Duplicating the rule shape here would
 * create a second, drifting definition of the rule language and put protocol
 * vocabulary into a package the engine is deliberately independent of; the
 * gateway validates against the real engine on write, which is the only place
 * the answer is authoritative.
 */

export type FirewallRulesetScope = 'shared' | 'binding';

/** One structured problem from `validateFirewallRuleset` / `validateFirewallCatalog`. */
export interface FirewallRulesetIssue {
  readonly code: string;
  readonly message: string;
  readonly rulesetId?: string;
  readonly ruleId?: string;
  readonly at?: string;
}

export interface FirewallRulesetSummary {
  readonly id: string;
  readonly name: string;
  readonly scope: FirewallRulesetScope;
  readonly owner_key_bucket_id: string | null;
  readonly owner_channel_id: string | null;
  /** `null` = armed at nothing; the gateway reads the reference as absent. */
  readonly active_version: number | null;
  readonly latest_version: number | null;
  readonly created_at: string;
  readonly updated_at: string;
}

export interface FirewallRulesetVersionRecord {
  readonly ruleset_id: string;
  readonly version: number;
  readonly doc_version: number;
  readonly extends_ruleset_id: string | null;
  /** The rule array as authored. Opaque here; validated by the engine. */
  readonly rules: unknown;
  /** `hashRuleChain` over the RESOLVED chain, computed at write time. */
  readonly resolved_hash: string;
  readonly note: string | null;
  readonly created_by: string | null;
  readonly created_at: string;
}

/** Version list entry — the same row without the document. */
export type FirewallRulesetVersionSummary = Omit<FirewallRulesetVersionRecord, 'rules'>;

export interface ListFirewallRulesetsResponse {
  readonly rulesets: readonly FirewallRulesetSummary[];
}

export interface GetFirewallRulesetResponse {
  readonly ruleset: FirewallRulesetSummary;
  readonly versions: readonly FirewallRulesetVersionSummary[];
}

export interface GetFirewallRulesetVersionResponse {
  readonly version: FirewallRulesetVersionRecord;
}

export interface CreateFirewallRulesetRequest {
  readonly name: string;
  readonly scope?: FirewallRulesetScope;
  readonly owner_key_bucket_id?: string | null;
  readonly owner_channel_id?: string | null;
  readonly doc_version: number;
  readonly extends_ruleset_id?: string | null;
  readonly rules: unknown;
  readonly note?: string | null;
}

export interface CreateFirewallRulesetVersionRequest {
  readonly doc_version: number;
  readonly extends_ruleset_id?: string | null;
  readonly rules: unknown;
  readonly note?: string | null;
}

export interface ActivateFirewallRulesetRequest {
  /** `null` disarms the ruleset without deleting anything. */
  readonly version: number | null;
}

/**
 * Every mutating response echoes the gateway mutation receipt so ops-api can
 * mirror it into platform `ops_audit`.
 *
 * The receipt names the ruleset, the version and the hash and NOTHING ELSE.
 * A rule document in an audit row would put detection logic into a surface
 * with a different reader set and a different retention story — readability is
 * evadability, and an audit trail is not the place to publish it.
 */
export interface FirewallRulesetMutationResponse {
  readonly ruleset: FirewallRulesetSummary;
  readonly version?: FirewallRulesetVersionSummary;
  readonly mutation_receipt: unknown;
}

export interface DeleteFirewallRulesetResponse {
  readonly deleted: true;
  readonly mutation_receipt: unknown;
}

/** 400 body for a write that failed validation: the FULL issue list. */
export interface FirewallRulesetValidationErrorBody {
  readonly error: {
    readonly code: 'FIREWALL_RULESET_INVALID';
    readonly message: string;
    readonly retryable: boolean;
  };
  readonly issues: readonly FirewallRulesetIssue[];
}

/* -------------------------------------------------------------------------- */
/* remote replay (#1759)                                                       */
/* -------------------------------------------------------------------------- */

/**
 * The caller-supplied context a request would have carried at the hook.
 *
 * A whitelist, not a passthrough. The runtime injects exactly these four keys
 * into the normalized document's `derived` block, so a rule may legitimately
 * read them and a replay that could not set them would under-report matches.
 * Anything else a caller sends is dropped: an open `derived` channel would let
 * the submitter invent a field a rule reads and steer the verdict, which is a
 * bypass dressed as a convenience. The engine already refuses to let a caller
 * set `provenance` / `detectionLevel` / `wireShape`; this keeps the rest closed
 * on the same principle.
 */
export interface FirewallReplaySampleContext {
  readonly client_format?: string | null;
  readonly lane?: string | null;
  readonly stream?: boolean | null;
  readonly endpoint?: string | null;
}

/**
 * The context that was actually applied to one sample, echoed back on every
 * result.
 *
 * Non-optional, all four keys, because the failure this exists to prevent is
 * silent: a caller who writes `Lane` instead of `lane` used to get a report
 * full of `passthrough` verdicts and `zero_difference: true` for a rule that
 * would fire on every cc-lane request in production. The request is now refused
 * outright, and this echo makes the applied context a visible fact rather than
 * an implied default, so "why did nothing match" is answerable from the report.
 */
export interface FirewallReplayAppliedContext {
  readonly client_format: string | null;
  readonly lane: string | null;
  readonly stream: boolean | null;
  readonly endpoint: string | null;
}

/**
 * One raw request to replay. Headers and body arrive as they were sent, minus
 * whatever the local collection tool substituted.
 *
 * Redaction is the CALLER's job and it must be substitution rather than
 * deletion — `exists $.headers.x-api-key` has to stay true for a request that
 * carried one. The endpoint never reads a credential value and never stores a
 * sample, but it also cannot verify that the caller redacted; see the replay
 * record's provenance note.
 */
export interface FirewallReplaySample {
  readonly headers?: Readonly<Record<string, string | readonly string[]>>;
  readonly body?: unknown;
  readonly context?: FirewallReplaySampleContext;
}

export interface ReplayFirewallRulesetRequest {
  /** The version to judge with. Any version, including an unactivated draft. */
  readonly version: number;
  /**
   * Optional ASSERTION about which version is currently armed. The server
   * always uses its own read; a mismatch is refused rather than honoured, so a
   * caller cannot choose the baseline its report is measured against.
   */
  readonly baseline_version?: number | null;
  readonly samples: readonly FirewallReplaySample[];
}

/** A sample the engine refused to judge is `error` — never silently a pass. */
export type FirewallReplayOutcome = 'passthrough' | 'match' | 'error';

/**
 * One verdict, with the matched rule's `description` deliberately absent.
 *
 * Same discipline as the pre-dispatch fact snapshot: a description is prose
 * ABOUT the criterion and is the one field on a rule that restates it in
 * readable form. The rule id is enough to find it in a surface that is already
 * allowed to show it.
 */
export interface FirewallReplayVerdict {
  readonly outcome: FirewallReplayOutcome;
  readonly rule_id: string | null;
  readonly ruleset_id: string | null;
  readonly action: 'allow' | 'deny' | 'synthesize' | null;
  readonly deny_as: string | null;
  readonly synthesize_as: string | null;
  /** Engine error CLASS when `outcome` is `error`. Never an exception message. */
  readonly error_class: string | null;
}

/**
 * How one sample's two verdicts differ.
 *
 * `pass-to-intercept` is called out separately from every other change because
 * it is the only direction that can refuse a request that works today, and
 * refusing good traffic is the failure this whole gate exists to catch.
 */
export type FirewallReplayDiff =
  | 'unchanged'
  | 'pass-to-intercept'
  | 'intercept-to-pass'
  | 'action-changed'
  | 'rule-changed'
  | 'errored';

export interface FirewallReplaySampleResult {
  readonly index: number;
  /** What `$.derived.*` actually held while this sample was judged. */
  readonly applied_context: FirewallReplayAppliedContext;
  readonly candidate: FirewallReplayVerdict;
  /** `null` when there was no baseline to compare against. */
  readonly baseline: FirewallReplayVerdict | null;
  readonly diff: FirewallReplayDiff | null;
}

/**
 * The counts a replay record stores. This is the whole of what is persisted
 * about a corpus — no header, no body, no rule id, no sample.
 *
 * `zero_difference` is `null` rather than `true` when no comparison ran. A
 * boolean there would let "nothing was compared" read as "nothing changed",
 * which is the silent-zero the acceptance criteria forbid.
 */
export interface FirewallReplaySummary {
  readonly sample_count: number;
  readonly baseline_version: number | null;
  /** `false` when there is no armed baseline, or its chain no longer resolves. */
  readonly compared: boolean;
  readonly baseline_unresolvable: boolean;
  readonly zero_difference: boolean | null;
  readonly pass_to_intercept: number;
  readonly intercept_to_pass: number;
  readonly action_changed: number;
  readonly rule_changed: number;
  readonly unchanged: number;
  readonly errored: number;
}

/**
 * The stored evidence an activation cites. Written by the endpoint from what it
 * actually computed — no field here is taken from the request body.
 */
export interface FirewallReplayRecord {
  readonly id: string;
  readonly ruleset_id: string;
  readonly version: number;
  readonly baseline_version: number | null;
  /**
   * The hash of the chain as RESOLVED at replay time, not the version row's
   * write-time hash. A parent activation legitimately changes a child's
   * flattened chain, so the write-time hash can be stale while the judgement
   * that would run is different; the gate recomputes this same quantity.
   */
  readonly candidate_resolved_hash: string;
  readonly sample_count: number;
  readonly corpus_fingerprint: string;
  readonly summary: FirewallReplaySummary;
  readonly created_by: string | null;
  readonly created_at: string;
}

export interface ReplayFirewallRulesetResponse {
  readonly record: FirewallReplayRecord;
  readonly samples: readonly FirewallReplaySampleResult[];
}

/**
 * Per-call sample ceiling. The stratified corpus #1759 describes is ~1000
 * samples and 27 distinct signatures; this leaves room for that without
 * letting one admin call turn into an unbounded evaluation loop.
 */
export const FIREWALL_REPLAY_MAX_SAMPLES = 2000;

/**
 * ── Replay size ceilings: RESOURCE SELF-DEFENCE, not transport fidelity ──────
 *
 * These numbers exist for one reason: the gateway process that answers a replay
 * is the same process that serves every paying tenant's generation traffic, and
 * a replay body is held whole in memory and judged in one synchronous loop that
 * never yields. Measured before these caps existed, 2000 samples of 256 KiB was
 * a 500 MB body and a 2.5 GB resident heap, accepted without complaint. One
 * mispaste could have taken the ingress down.
 *
 * They are deliberately NOT a model of what an upstream will accept. Real
 * transport limits (Node's header block, Caddy's request ceiling) are deployment
 * facts that live in another repository and move with configuration; hard-coding
 * them here would make the replay an authority on something it cannot observe,
 * and it would start lying the first time an operator retunes a proxy. A sample
 * that fits these ceilings may still be rejected in production before a rule
 * ever sees it — the replay does not claim otherwise.
 */
export const FIREWALL_REPLAY_MAX_CORPUS_BYTES = 16 * 1024 * 1024;

/** Per sample, measured over its normalized form. Fits a large agent request. */
export const FIREWALL_REPLAY_MAX_SAMPLE_BYTES = 256 * 1024;

/** Per header value. Bounds one pathological string, not a wire limit. */
export const FIREWALL_REPLAY_MAX_HEADER_VALUE_BYTES = 32 * 1024;

/** Per sample. A real request carries tens; hundreds is a generated payload. */
export const FIREWALL_REPLAY_MAX_HEADERS_PER_SAMPLE = 200;

/**
 * The whole request body, enforced while it streams.
 *
 * This is the only one of these numbers that actually protects the heap: the
 * others are checked after the body has been parsed, by which point the memory
 * is already committed. Set above the normalized-corpus ceiling on purpose, so
 * a corpus that is merely too big fails with the message that names which
 * sample and which limit, and this one only catches what never should have been
 * sent at all.
 */
export const FIREWALL_REPLAY_MAX_REQUEST_BYTES = 20 * 1024 * 1024;

/**
 * Audit action literals for the ruleset surface. `activate` is deliberately
 * its own action rather than a flavour of `update`: it is the only one of the
 * four that changes what a live request sees.
 */
export const FIREWALL_RULESET_AUDIT_ACTIONS = [
  'firewall_ruleset.create',
  'firewall_ruleset.version.create',
  'firewall_ruleset.activate',
  'firewall_ruleset.delete',
] as const;

export type FirewallRulesetAuditAction = (typeof FIREWALL_RULESET_AUDIT_ACTIONS)[number];

/** Receipt `kind` for every ruleset mutation. */
export const FIREWALL_RULESET_RECEIPT_KIND = 'firewall_ruleset';
